import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  Table2,
  TrendingDown,
  TrendingUp,
  UsersRound,
  Wallet,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIERS = [
  { label: "< Rp 1 Juta", min: 0, max: 1000000, color: "#3368A0" },
  { label: "Rp 1–2 Juta", min: 1000000, max: 2000000, color: "#66A3BF" },
  { label: "Rp 2–3 Juta", min: 2000000, max: 3000000, color: "#93C5FD" },
  { label: "> Rp 3 Juta", min: 3000000, max: Infinity, color: "#F59E0B" },
];

const compactRupiah = (value) => {
  if (!value) {
    return "Rp 0";
  }
  if (Math.abs(value) >= 1000000) {
    return `Rp ${(value / 1000000).toLocaleString("id-ID", {
      maximumFractionDigits: 1,
    })} jt`;
  }
  if (Math.abs(value) >= 1000) {
    return `Rp ${(value / 1000).toLocaleString("id-ID", {
      maximumFractionDigits: 0,
    })} rb`;
  }
  return rupiah(value);
};

const totalDeduction = (row) => (
  (row.total_potongan || 0)
  + (row.total_cicilan || 0)
  + (row.total_reject_penalty || 0)
);

const RankingTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0].payload;
  return (
    <div
      className="border border-slate-200 bg-white px-3 py-2 shadow-lg"
      data-testid="disbursement-ranking-tooltip"
    >
      <p className="text-sm font-semibold text-slate-800">{row.user_name}</p>
      <p className="mt-1 text-xs text-slate-500">{row.outlet_name}</p>
      <p className="mt-1 text-sm font-bold text-[#3368A0]">
        {rupiah(row.gaji_bersih)}
      </p>
    </div>
  );
};

const DistributionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0].payload;
  return (
    <div
      className="border border-slate-200 bg-white px-3 py-2 shadow-lg"
      data-testid="disbursement-distribution-tooltip"
    >
      <p className="text-sm font-semibold text-slate-800">{row.label}</p>
      <p className="mt-1 text-xs text-slate-500">{row.count} karyawan</p>
      <p className="mt-1 text-sm font-bold text-[#3368A0]">
        {rupiah(row.total)}
      </p>
    </div>
  );
};

export default function DisbursementPanel({
  summary,
  sumMonth,
  year,
  months,
  onMonthChange,
  onDownload,
}) {
  const [view, setView] = useState("ranking");
  const [rankingMode, setRankingMode] = useState("top10");
  const [outletFilter, setOutletFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [tablePage, setTablePage] = useState(1);

  const rows = useMemo(() => summary?.rows || [], [summary]);
  const outletOptions = useMemo(
    () => [...new Set(rows.map((row) => row.outlet_name).filter(Boolean))].sort(),
    [rows],
  );
  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesOutlet = outletFilter === "all" || row.outlet_name === outletFilter;
      const matchesSearch = !normalizedSearch
        || row.user_name.toLowerCase().includes(normalizedSearch);
      return matchesOutlet && matchesSearch;
    });
  }, [outletFilter, rows, search]);
  const rankedRows = useMemo(
    () => [...filteredRows].sort((a, b) => b.gaji_bersih - a.gaji_bersih),
    [filteredRows],
  );
  const averageSalary = filteredRows.length
    ? filteredRows.reduce((total, row) => total + row.gaji_bersih, 0) / filteredRows.length
    : 0;
  const distributionRows = useMemo(
    () => TIERS.map((tier) => {
      const tierRows = filteredRows.filter((row) => (
        row.gaji_bersih >= tier.min && row.gaji_bersih < tier.max
      ));
      return {
        ...tier,
        count: tierRows.length,
        total: tierRows.reduce((total, row) => total + row.gaji_bersih, 0),
      };
    }),
    [filteredRows],
  );

  const rankingRows = useMemo(() => {
    if (rankingMode === "bottom10") {
      return [...rankedRows].reverse().slice(0, 10).reverse();
    }
    return rankedRows.slice(0, rankingMode === "top20" ? 20 : 10).reverse();
  }, [rankedRows, rankingMode]);

  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(rankedRows.length / pageSize));
  const paginatedRows = rankedRows.slice((tablePage - 1) * pageSize, tablePage * pageSize);
  const maximumSalary = rankedRows[0]?.gaji_bersih || 1;
  const rankingChartHeight = Math.max(280, rankingRows.length * 42 + 54);

  useEffect(() => {
    setTablePage(1);
  }, [outletFilter, search, sumMonth]);

  const emptyState = (
    <div
      className="flex min-h-56 items-center justify-center border border-dashed border-slate-200"
      data-testid="disbursement-empty-state"
    >
      <p className="text-sm text-slate-500">Tidak ada data karyawan untuk filter ini.</p>
    </div>
  );

  return (
    <section
      className="space-y-5 border border-[#C8DFDB] bg-white p-5"
      data-testid="payroll-summary-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-[#3368A0]" />
            <h3 className="text-lg font-semibold text-slate-800">
              Ringkasan Pengeluaran Gaji
            </h3>
          </div>
          <p className="mt-1 text-xs text-slate-500" data-testid="disbursement-period">
            Periode {summary.period_start} s/d {summary.period_end}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(sumMonth)} onValueChange={(value) => onMonthChange(Number(value))}>
            <SelectTrigger className="w-40" data-testid="disbursement-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((monthName, index) => (
                <SelectItem key={monthName} value={String(index + 1)}>
                  {monthName} {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={onDownload}
            data-testid="download-disbursement-button"
            className="bg-[#66A3BF] hover:bg-[#5590ab]"
          >
            <Download className="mr-1.5 h-4 w-4" />
            Download XLS
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="bg-[#3368A0] p-4 text-white" data-testid="total-net-card">
          <p className="flex items-center gap-1.5 text-xs text-white/75">
            <Banknote className="h-4 w-4" />
            Total Gaji Bersih
          </p>
          <p className="mt-1 text-xl font-extrabold sm:text-2xl">{rupiah(summary.total_net)}</p>
        </div>
        <div className="bg-[#66A3BF] p-4 text-white" data-testid="total-loans-card">
          <p className="flex items-center gap-1.5 text-xs text-white/80">
            <Wallet className="h-4 w-4" />
            Sisa Pinjaman
          </p>
          <p className="mt-1 text-xl font-extrabold sm:text-2xl">
            {rupiah(summary.total_outstanding_loans)}
          </p>
        </div>
        <div
          className="border border-slate-200 bg-slate-50 p-4"
          data-testid="avg-salary-card"
        >
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <TrendingUp className="h-4 w-4 text-[#3368A0]" />
            Rata-rata Gaji
          </p>
          <p className="mt-1 text-xl font-extrabold text-slate-800 sm:text-2xl">
            {rupiah(Math.round(averageSalary))}
          </p>
        </div>
        <div
          className="border border-slate-200 bg-slate-50 p-4"
          data-testid="total-emp-card"
        >
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <UsersRound className="h-4 w-4 text-[#3368A0]" />
            Karyawan Berhak Gaji
          </p>
          <p className="mt-1 text-xl font-extrabold text-slate-800 sm:text-2xl">
            {filteredRows.length}
          </p>
        </div>
      </div>

      <div className="border-y border-slate-100 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div
            className={
              "flex w-full overflow-x-auto border border-slate-200 bg-slate-50 p-1 xl:w-auto"
            }
            data-testid="disbursement-view-tabs"
          >
            <button
              type="button"
              onClick={() => setView("ranking")}
              data-testid="disbursement-view-toggle-top10"
              className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-semibold
                transition-colors ${
                view === "ranking"
                  ? "bg-[#3368A0] text-white"
                  : "text-slate-600 hover:bg-white"
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              Top & Peringkat
            </button>
            <button
              type="button"
              onClick={() => setView("distribution")}
              data-testid="disbursement-view-toggle-distribusi"
              className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-semibold
                transition-colors ${
                view === "distribution"
                  ? "bg-[#3368A0] text-white"
                  : "text-slate-600 hover:bg-white"
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              Distribusi Gaji
            </button>
            <button
              type="button"
              onClick={() => setView("table")}
              data-testid="disbursement-view-toggle-tabel"
              className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-semibold
                transition-colors ${
                view === "table"
                  ? "bg-[#3368A0] text-white"
                  : "text-slate-600 hover:bg-white"
              }`}
            >
              <Table2 className="h-4 w-4" />
              Semua Karyawan
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select value={outletFilter} onValueChange={setOutletFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="disbursement-outlet-filter">
                <SelectValue placeholder="Semua outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua outlet</SelectItem>
                {outletOptions.map((outlet) => (
                  <SelectItem key={outlet} value={outlet}>{outlet}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Cari karyawan"
                data-testid="disbursement-search-input"
                className="w-full pl-9 sm:w-52"
              />
            </div>
          </div>
        </div>
      </div>

      {view === "ranking" && (
        <div data-testid="disbursement-chart-view">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-700">
                {rankingMode === "bottom10" ? "10 Gaji Bersih Terendah" : "Peringkat Gaji Bersih"}
              </h4>
              <p className="mt-1 text-xs text-slate-500">
                Menampilkan sebagian data agar tetap mudah dibandingkan.
              </p>
            </div>
            <div
              className="flex border border-slate-200 p-1"
              data-testid="disbursement-ranking-controls"
            >
              <button
                type="button"
                onClick={() => setRankingMode("top10")}
                data-testid="disbursement-top-10-button"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  rankingMode === "top10" ? "bg-slate-800 text-white" : "text-slate-600"
                }`}
              >
                Top 10
              </button>
              <button
                type="button"
                onClick={() => setRankingMode("top20")}
                data-testid="disbursement-top-20-button"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  rankingMode === "top20" ? "bg-slate-800 text-white" : "text-slate-600"
                }`}
              >
                Top 20
              </button>
              <button
                type="button"
                onClick={() => setRankingMode("bottom10")}
                data-testid="disbursement-bottom-10-button"
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold ${
                  rankingMode === "bottom10" ? "bg-slate-800 text-white" : "text-slate-600"
                }`}
              >
                <TrendingDown className="h-3.5 w-3.5" />
                Terendah
              </button>
            </div>
          </div>
          {rankingRows.length ? (
            <div
              className="w-full"
              style={{ height: `${rankingChartHeight}px` }}
              data-testid="disbursement-chart-container"
            >
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={0}
                minHeight={280}
              >
                <BarChart
                  data={rankingRows}
                  layout="vertical"
                  margin={{ top: 2, right: 18, bottom: 4, left: 0 }}
                >
                  <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    tick={{ fill: "#94a3b8", fontSize: 10 }}
                    tickFormatter={compactRupiah}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    dataKey="user_name"
                    type="category"
                    width={112}
                    tick={{ fill: "#475569", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip cursor={{ fill: "#f8fafc" }} content={<RankingTooltip />} />
                  <Bar
                    dataKey="gaji_bersih"
                    name="Gaji Bersih"
                    fill="#3368A0"
                    radius={[0, 4, 4, 0]}
                    barSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : emptyState}
        </div>
      )}

      {view === "distribution" && (
        <div data-testid="disbursement-distribution-view">
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">Sebaran Gaji Bersih</h4>
            <p className="mt-1 text-xs text-slate-500">
              Bandingkan jumlah karyawan pada setiap rentang gaji.
            </p>
          </div>
          {filteredRows.length ? (
            <>
              <div className="h-72" data-testid="disbursement-chart-container">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={280}>
                  <BarChart
                    data={distributionRows}
                    margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#475569", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "#94a3b8", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "#f8fafc" }}
                      content={<DistributionTooltip />}
                    />
                    <Bar
                      dataKey="count"
                      name="Jumlah Karyawan"
                      radius={[4, 4, 0, 0]}
                      barSize={36}
                    >
                      {distributionRows.map((tier) => (
                        <Cell key={tier.label} fill={tier.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
                {distributionRows.map((tier) => (
                  <div
                    key={tier.label}
                    className="border-l-4 border-slate-200 bg-slate-50 px-3 py-2"
                    style={{ borderLeftColor: tier.color }}
                    data-testid={
                      `disbursement-tier-${tier.label.replace(/[^a-z0-9]/gi, "-")}`
                    }
                  >
                    <p className="text-xs font-medium text-slate-600">{tier.label}</p>
                    <p className="mt-1 text-sm font-bold text-slate-800">
                      {tier.count} karyawan
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">{rupiah(tier.total)}</p>
                  </div>
                ))}
              </div>
            </>
          ) : emptyState}
        </div>
      )}

      {view === "table" && (
        <div data-testid="disbursement-table-view">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-700">Daftar Disbursement</h4>
              <p className="mt-1 text-xs text-slate-500">
                {filteredRows.length} karyawan ditemukan, diurutkan dari gaji bersih tertinggi.
              </p>
            </div>
            <p
              className="text-xs font-medium text-slate-500"
              data-testid="disbursement-table-page-label"
            >
              Halaman {tablePage} dari {pageCount}
            </p>
          </div>
          {paginatedRows.length ? (
            <>
              <div className="overflow-x-auto" data-testid="disbursement-detail-table">
                <table className="w-full min-w-[720px] text-sm">
                  <thead
                    className={[
                      "border-y border-slate-200 bg-slate-50 text-left text-xs uppercase",
                      "text-slate-500",
                    ].join(" ")}
                  >
                    <tr>
                      <th className="px-3 py-3">#</th>
                      <th className="px-3 py-3">Karyawan</th>
                      <th className="px-3 py-3">Outlet</th>
                      <th className="px-3 py-3 text-center">Hadir</th>
                      <th className="px-3 py-3 text-right">Potongan</th>
                      <th className="px-3 py-3 text-right">Gaji Bersih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, index) => {
                      const rank = (tablePage - 1) * pageSize + index + 1;
                      const width = Math.max(3, (row.gaji_bersih / maximumSalary) * 100);
                      return (
                        <tr key={row.user_name} className="border-b border-slate-100">
                          <td className="px-3 py-3 font-semibold text-slate-400">{rank}</td>
                          <td className="px-3 py-3">
                            <p className="font-semibold text-slate-700">{row.user_name}</p>
                            <div className="mt-1 h-1.5 w-28 bg-slate-100">
                              <div
                                className="h-full bg-[#3368A0]"
                                style={{ width: `${width}%` }}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-3 text-slate-500">{row.outlet_name}</td>
                          <td className="px-3 py-3 text-center text-slate-600">{row.hari_hadir}</td>
                          <td className="px-3 py-3 text-right text-rose-600">
                            {rupiah(totalDeduction(row))}
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-[#3368A0]">
                            {rupiah(row.gaji_bersih)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setTablePage((page) => Math.max(1, page - 1))}
                  disabled={tablePage === 1}
                  data-testid="disbursement-table-prev-button"
                  aria-label="Halaman sebelumnya"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setTablePage((page) => Math.min(pageCount, page + 1))}
                  disabled={tablePage === pageCount}
                  data-testid="disbursement-table-next-button"
                  aria-label="Halaman berikutnya"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : emptyState}
        </div>
      )}
    </section>
  );
}